import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Center, Heading, Spinner, VStack, HStack, Flex, Stack, Icon } from '@chakra-ui/react';
import { ConversionGate } from '@src/components/sco/Conversion/controller/ConversionGate';
import { useEducationContext } from '@src/components/sco/Education/EducationContext';
import { EducationErrorState } from '@src/components/sco/Education/EducationErrorState';
import { PodcastSubscribe } from '@src/components/sco/Education/PodcastLanding/PodcastSubscribe';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { BoxedCardCTA } from '@src/components/sco/UserAccessGate/cta-variants/BoxedCardCTA';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { PodcastEpisode } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { useParams } from 'next/navigation';
import { useQueryState } from 'nuqs';
import React from 'react';
import { RiRobot2Line } from 'react-icons/ri';
export const PodcastDetail = props => {
  const $ = _c(36);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const educationContext = useEducationContext();
  const routerParams = useParams();
  const {
    currentUser
  } = useCurrentUser();
  const [, setPrompt] = useQueryState("prompt");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($slug: String) {
        podcastEpisode(where: { slug: $slug }) {
          id
          title
          description
          relatedUrl
          automatedTranscript {
            markdown
          }
          transcript {
            markdown
          }
          posterImage {
            id
            url
            altText
            height
            width
          }
          number
          megaphoneLink
          transistorLink
          iTunesLink
          tuneInLink
          spotifyLink
          stitcherLink
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = routerParams?.slug;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      variables: {
        slug: t1
      }
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const podcastQuery = useQuery(t0, t2);
  const episode = podcastQuery.data?.podcastEpisode;
  let t3;
  if ($[3] !== educationContext || $[4] !== episode) {
    t3 = () => {
      if (episode) {
        educationContext.setActiveContentItem(episode);
      }
    };
    $[3] = educationContext;
    $[4] = episode;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = episode?.id;
  let t5;
  if ($[6] !== t4) {
    t5 = [t4];
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useUniversalEffect(t3, t5);
  let t6;
  if ($[8] !== educationContext || $[9] !== episode?.number || $[10] !== episode?.slug) {
    t6 = () => {
      educationContext.setBreadcrumbs([{
        title: "Podcast",
        href: "/podcast"
      }, {
        title: `Episode ${episode?.number || "..."}`,
        href: `/podcast/${episode?.slug}`
      }]);
    };
    $[8] = educationContext;
    $[9] = episode?.number;
    $[10] = episode?.slug;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = episode?.number;
  let t8;
  if ($[12] !== props.slug || $[13] !== t7) {
    t8 = [props.slug, t7];
    $[12] = props.slug;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useUniversalEffect(t6, t8);
  const transcriptMarkdown = episode?.transcript?.markdown || episode?.automatedTranscript?.markdown;
  if (podcastQuery.loading) {
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Center padding="1em"><Spinner /></Center>;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    return t9;
  }
  if (podcastQuery.error) {
    const t9 = podcastQuery.error ? podcastQuery.error.message : "Podcast episode not found";
    let t10;
    if ($[16] !== t9) {
      t10 = <p>{"Error: "}{t9}</p>;
      $[16] = t9;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    return t10;
  }
  if (!podcastQuery.loading && !episode) {
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <EducationErrorState ctaHref="/podcast" ctaLabel="Explore All Episodes" headingText="Podcast episode not found." />;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  let t9;
  if ($[19] !== podcastQuery.loading) {
    t9 = podcastQuery.loading && <Center padding="1em"><Spinner /></Center>;
    $[19] = podcastQuery.loading;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== podcastQuery.error) {
    t10 = podcastQuery.error && <p>{"Error: "}{podcastQuery.error.message}</p>;
    $[21] = podcastQuery.error;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== currentUser?.contentPermissions?.canAccessCourses || $[24] !== currentUser?.isGuest || $[25] !== currentUser?.isPasswordSet || $[26] !== currentUser?.isRegistered || $[27] !== episode || $[28] !== getBreakpointValue || $[29] !== setPrompt || $[30] !== transcriptMarkdown) {
    t11 = episode && <ViewableTracker trackScrolling={true} viewableId={episode.id} viewableType={episode.__typename}><Stack align="center" direction="column" gap={0} width="full"><VStack align="center" height="full" justify="flex-end" maxWidth="container.lg" position="relative" width="full"><VStack gap={getBreakpointValue([4, 6])} width="full" zIndex={1}><Stack flexDirection={["column", "row"]} gap="20px" width="full" zIndex={4}>{!episode.transistorLink && episode.megaphoneLink && <iframe height="200" src={episode.megaphoneLink} width="100%" style={{
                borderRadius: "0.75em"
              }} />}{episode.transistorLink && <Box borderRadius="0.75em" boxShadow="lg" style={{
                height: "180px",
                width: "100%"
              }}><iframe height="180" seamless={true} src={episode.transistorLink} width="100%" style={{
                  borderRadius: "0.75em",
                  width: "100%",
                  height: "180px"
                }} /></Box>}</Stack><PodcastSubscribe iTunesLink={episode.iTunesLink} spotifyLink={episode.spotifyLink} tuneInLink={episode.tuneInLink} /></VStack></VStack><Box maxWidth="container.md" paddingY="1.5em" width="full">{currentUser?.isGuest && <BoxedCardCTA boxProps={{
            bgColor: "actionActiveSecondary"
          }} title="Find this helpful?" emailCaptureProps={{
            afterSuccess: async () => {
              if (currentUser?.isPasswordSet) {
                await setPrompt("welcome");
              } else {
                await setPrompt("register");
              }
            },
            inputProps: {
              colorScheme: "whiteAlpha"
            }
          }} subtitle={<>This is just a small sample! Register to unlock our in-depth courses, hundreds of video courses, and a library of playbooks and articles to grow your startup fast. Let us show you!</>} />}{currentUser?.isRegistered && !currentUser?.contentPermissions?.canAccessCourses && <Box bgColor="actionActiveSecondary" borderRadius="lg" width="full"><ConversionGate density="boxed" headline="Find this helpful? There's so much more!" intent="content-lock" permission="canAccessCourses" /></Box>}</Box>{transcriptMarkdown && <Flex justify="center" marginBottom={20} padding={10} width="full"><VStack align="flex-start" gap={10} maxWidth="container.md" width="full"><HStack align="center" gap={3}><Icon asChild={true} boxSize={6} color="primary"><RiRobot2Line /></Icon><Heading fontWeight={600} size="xl">AI Generated Transcript</Heading></HStack><MarkdownRenderer preset="edu">{transcriptMarkdown}</MarkdownRenderer></VStack></Flex>}</Stack></ViewableTracker>;
    $[23] = currentUser?.contentPermissions?.canAccessCourses;
    $[24] = currentUser?.isGuest;
    $[25] = currentUser?.isPasswordSet;
    $[26] = currentUser?.isRegistered;
    $[27] = episode;
    $[28] = getBreakpointValue;
    $[29] = setPrompt;
    $[30] = transcriptMarkdown;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t9) {
    t12 = <VStack align="center" justify="stretch" marginBottom={20} padding={10} width="full">{t9}{t10}{t11}</VStack>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};